import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, theme } from 'antd';
import { arrayMove } from '@dnd-kit/sortable';
import { useApp } from 'contexts/app';
import ImageService from 'services/image';
import ImagePickerContext from '../ImagePickerContext';

const { useToken } = theme;

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const formatBytes = (bytes) => {
  if (!Number.isFinite(Number(bytes)) || Number(bytes) <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const size = Number(bytes);
  const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const formatted = size / 1024 ** unitIndex;

  return `${formatted >= 10 || unitIndex === 0 ? formatted.toFixed(0) : formatted.toFixed(1)} ${units[unitIndex]}`;
};

const normalizeAcceptFormats = (formats) => {
  if (!formats) {
    return 'image/jpeg,image/png,image/webp';
  }

  if (Array.isArray(formats)) {
    return formats.join(',');
  }

  return formats;
};

const isAcceptedFileType = (file, formats) => {
  if (!formats || (Array.isArray(formats) && formats.length === 0)) {
    return true;
  }

  const rules = Array.isArray(formats) ? formats : String(formats).split(',');
  const fileType = String(file?.type || '').toLowerCase();
  const fileName = String(file?.name || '').toLowerCase();

  return rules
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => {
      if (rule === 'image/*') {
        return fileType.startsWith('image/');
      }

      if (rule.startsWith('.')) {
        return fileName.endsWith(rule);
      }

      if (rule.endsWith('/*')) {
        return fileType.startsWith(rule.replace('*', ''));
      }

      return fileType === rule;
    });
};

const normalizeFileList = (files = []) =>
  files.map((file, index) => ({
    ...file,
    uid: file?.uid || String(file?.id || file?.url || file?.sm_url || `picker-file-${index}`),
  }));

const getFileIdentity = (file) =>
  String(file?.uid || file?.id || file?.url || file?.sm_url || '');

export const getPreviewSource = (file) => file?.url || file?.preview || file?.sm_url || '';

const useComponent = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { token } = useToken();
  const { notification } = useApp();
  const { value, maxPhotos, acceptedFormats, maxFileSize, onChange } = useContext(ImagePickerContext);

  const panelRef = useRef(null);
  const fileListRef = useRef([]);
  const uploadQueueRef = useRef(Promise.resolve());
  const limitErrorShownRef = useRef(false);

  useEffect(() => {
    setFileList(normalizeFileList(Array.isArray(value) ? value : []));
  }, [value]);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  useEffect(() => {
    if (!previewOpen) {
      return;
    }

    const currentFile = fileList[currentIndex];
    if (currentFile) {
      setPreviewImage(getPreviewSource(currentFile));
    }
  }, [currentIndex, fileList, previewOpen]);

  const panelStyle = useMemo(() => ({
    '--picker-border-color': token.colorBorderSecondary,
    '--picker-fill-color': token.colorFillAlter,
    '--picker-primary-color': token.colorPrimary,
    '--picker-shadow-color': token.colorPrimaryBg,
  }), [token.colorBorderSecondary, token.colorFillAlter, token.colorPrimary, token.colorPrimaryBg]);

  const showLimitError = () => {
    if (!limitErrorShownRef.current) {
      notification.error(`You can only upload up to ${maxPhotos} files`);
      limitErrorShownRef.current = true;
      setTimeout(() => {
        limitErrorShownRef.current = false;
      }, 0);
    }
  };

  const uploadImage = async (file) => {
    const imageService = new ImageService();
    const formData = new FormData();
    formData.append('file', file);
    return imageService.upload(formData);
  };

  const uploadFiles = async (files) => {
    const imageFiles = (files || []).filter(Boolean);

    if (imageFiles.length === 0) {
      return;
    }

    setLoading(true);

    try {
      let nextFileList = [...fileListRef.current];
      let hasChanges = false;

      for (const file of imageFiles) {
        if (nextFileList.length >= maxPhotos) {
          showLimitError();
          break;
        }

        if (!isAcceptedFileType(file, acceptedFormats)) {
          notification.error('File format is not supported');
          continue;
        }

        if (Number(file.size) > maxFileSize) {
          notification.error(`File size must be smaller than ${formatBytes(maxFileSize)}`);
          continue;
        }

        const result = await uploadImage(file);
        nextFileList = [...nextFileList, normalizeFileList([result])[0]];
        hasChanges = true;
      }

      if (hasChanges) {
        fileListRef.current = nextFileList;
        setFileList(nextFileList);
        onChange(nextFileList);
      }
    } catch (error) {
      notification.error('File was not uploaded. Please try again later');
    } finally {
      setLoading(false);
    }
  };

  const enqueueUpload = (files) => {
    uploadQueueRef.current = uploadQueueRef.current
      .catch(() => {})
      .then(() => uploadFiles(files));

    return uploadQueueRef.current;
  };

  const handleBeforeUpload = async (file) => {
    await enqueueUpload([file]);
    return Upload.LIST_IGNORE;
  };

  const handlePaste = async (event) => {
    const pastedFiles = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file) => file && String(file.type || '').startsWith('image/'));

    if (pastedFiles.length === 0) {
      return;
    }

    event.preventDefault();
    await enqueueUpload(pastedFiles);
  };

  const handlePreview = async (file) => {
    const index = fileList.findIndex((item) => item.uid === file.uid || item.id === file.id);
    setCurrentIndex(index);

    if (!getPreviewSource(file) && file.originFileObj) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(getPreviewSource(file));
    setPreviewOpen(true);
  };

  const handleRemove = (file) => {
    const removedIdentity = getFileIdentity(file);
    const newFileList = fileList.filter((item) => getFileIdentity(item) !== removedIdentity);
    setFileList(newFileList);
    onChange(newFileList);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    let fileListNew;

    setFileList((prev) => {
      const activeIndex = prev.findIndex((item) => item.uid === active.id);
      const overIndex = prev.findIndex((item) => item.uid === over.id);

      fileListNew = arrayMove(prev, activeIndex, overIndex);
      return fileListNew;
    });

    onChange(fileListNew);
  };

  return {
    acceptValue: useMemo(() => normalizeAcceptFormats(acceptedFormats), [acceptedFormats]),
    canAddMore: fileList.length < maxPhotos,
    currentIndex,
    fileList,
    handleBeforeUpload,
    handleChange: () => {},
    handleLibraryCancel: () => setIsModalOpen(false),
    handleLibraryOk: () => setIsModalOpen(false),
    handleNext: () => setCurrentIndex((prevIndex) => (prevIndex + 1) % fileList.length),
    handlePanelClick: () => panelRef.current?.focus(),
    handlePaste,
    handlePrev: () => setCurrentIndex((prevIndex) => (prevIndex - 1 + fileList.length) % fileList.length),
    handlePreview,
    handlePreviewVisibleChange: (visible) => setPreviewOpen(visible),
    handleRemove,
    isModalOpen,
    loading,
    onDragEnd,
    openLibrary: () => setIsModalOpen(true),
    panelRef,
    panelStyle,
    previewImage,
    previewOpen,
    setPreviewImage,
  };
};

export default useComponent;